import { FC, useEffect, useRef } from 'react';

import { useForm } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import Editor from '@monaco-editor/react';
import { DialogTitle } from '@mui/material';
import { usePages } from 'api/portal/usePagesApi';
import { EToasterStatus, showToast } from 'utils/showToast';
import * as z from 'zod';

import Button from 'components/Button/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  Form,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';
import { Spinner } from 'components/ui/spinner';

type BlocksJsonModalProps = {
  onClose: () => void;
  pageId: string;
};

export const BLOCKS_JSON_FORM_SCHEMA = z.object({
  blocks: z.string().transform((val, ctx) => {
    try {
      return JSON.parse(val);
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'blocks must be a valid JSON string'
      });
      return z.NEVER;
    }
  })
});

export const EditBlocksModal: FC<BlocksJsonModalProps> = ({
  pageId,
  onClose
}) => {
  const { getPageEditBlocks, updateBlocks } = usePages(pageId ?? undefined);
  const pageEditBlocks = getPageEditBlocks.data;

  const editorRef = useRef<any>(null);

  const blocksFormConfig = useForm<z.infer<typeof BLOCKS_JSON_FORM_SCHEMA>>({
    resolver: zodResolver(BLOCKS_JSON_FORM_SCHEMA),
    defaultValues: {
      blocks: ''
    },
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (pageEditBlocks?.blocks && blocksFormConfig) {
      const formattedBlocks = JSON.stringify(pageEditBlocks.blocks, null, 2);
      blocksFormConfig.reset({ blocks: formattedBlocks });
    }
  }, [pageEditBlocks?.blocks, blocksFormConfig]);

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [onClose]);

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty }
  } = blocksFormConfig;

  const handleBlocksJsonFormSubmit = async (
    data: z.infer<typeof BLOCKS_JSON_FORM_SCHEMA>
  ) => {
    try {
      if (!pageId) {
        throw new Error('Page ID is required');
      }
      updateBlocks.mutate(
        { blocks: data.blocks, pageId },
        {
          onSuccess: () => {
            showToast({
              message: 'Blocks updated successfully',
              status: EToasterStatus.SUCCESS
            });
            onClose();
          },
          onError: () => {
            showToast({
              message: 'Error updating blocks',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    } catch (error) {
      console.log('error', error);
      showToast({
        message: 'Error updating page',
        status: EToasterStatus.ERROR
      });
    }
  };

  if (getPageEditBlocks.isError) {
    showToast({
      message: 'Could not load page data',
      status: EToasterStatus.ERROR
    });
    onClose();
  }

  return (
    <Dialog open={!!pageId} onOpenChange={onClose}>
      <DialogOverlay className="bg-alpha-80" />
      <DialogContent className="bg-base-card overflow-hidden p-0 max-w-[646px] max-h-[680px] h-full w-full gap-0">
        <DialogHeader className="bg-base-muted py-4 px-6 flex flex-row items-center justify-between">
          <DialogTitle className="text-base-foreground text-p-l font-semibold">
            Edit Blocks
          </DialogTitle>
          <DialogClose asChild>
            <FontAwesomeIcon
              icon="fa-regular fa-xmark"
              className="!mt-0 text-base-secondaryForeground cursor-pointer"
            />
          </DialogClose>
        </DialogHeader>
        {getPageEditBlocks.isLoading ? (
          <div className="flex items-center justify-center h-[500px]">
            <Spinner className="size-12" />
          </div>
        ) : (
          <Form {...blocksFormConfig}>
            <FormField
              control={control}
              name="blocks"
              render={({ field }) => (
                <FormItem>
                  <div className="rounded border">
                    <Editor
                      height="500px"
                      defaultLanguage="json"
                      value={field.value}
                      onChange={(val) => field.onChange(val ?? '')}
                      onMount={(editor) => {
                        editorRef.current = editor;
                      }}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        formatOnPaste: true,
                        scrollBeyondLastLine: false,
                        formatOnType: true,
                        readOnly:
                          updateBlocks.isLoading || getPageEditBlocks.isLoading
                      }}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="bg-base-muted p-6">
              <DialogClose asChild>
                <Button type="button" variant="link">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                onClick={() => {
                  const editor = editorRef.current;
                  if (editor) {
                    editor.getAction('editor.action.formatDocument').run();
                  }
                }}
              >
                Format
              </Button>
              <Button
                type="submit"
                disabled={!isValid || !isDirty || !pageId}
                onClick={handleSubmit(handleBlocksJsonFormSubmit)}
              >
                Save
              </Button>
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
